import axios from 'axios'
import jwtDefaultConfig from './jwtDefaultConfig'
import { v4 as uuidv4 } from 'uuid'
import { store } from '@store/store'

export default class JwtService {
    jwtConfig = { ...jwtDefaultConfig }
    isAlreadyFetchingAccessToken = false
    subscribers = []
    _accessToken = null

    constructor(jwtOverrideConfig) {
        this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

        axios.interceptors.request.use(
            (config) => {
                config.withCredentials = true
                const url = config.url || ''
                const isRefresh = url.includes('auth/refresh')
                const token = this.getToken()
                if (token && !isRefresh) {
                    config.headers = config.headers || {}
                    config.headers.Authorization = `${this.jwtConfig.tokenType} ${token}`.trim()
                } else if (config?.headers?.Authorization) {
                    delete config.headers.Authorization
                }
                return config
            },
            (error) => Promise.reject(error)
        )

        axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                const { config, response } = error || {}
                if (!response) return Promise.reject(error)

                const originalRequest = config
                const url = originalRequest?.url || ''
                const is401 = response.status === 401
                const skipRefresh =
                    url.includes('api/auth/refresh') ||
                    url === this.jwtConfig.loginEndpoint ||
                    url.endsWith('/login')
                const isMeliAuthError = response?.data?.code?.startsWith('meli')
                const isInvalidCredentials = response?.data?.error === 'Invalid credentials'

                if (response.status === 403 && response?.data?.code === 'user_not_admin_exception') {
                    window.location.replace('/misc/not-authorized')
                    return Promise.reject(error)
                }

                if (is401 && !skipRefresh && !isMeliAuthError && isInvalidCredentials) {
                    if (!this.isAlreadyFetchingAccessToken) {
                        this.isAlreadyFetchingAccessToken = true
                        try {
                            const r = await this.refreshToken()
                            const newToken = r?.data?.access_token || r?.data?.accessToken || null
                            if (newToken) {
                                this.setToken(newToken)
                                this.onAccessTokenFetched(newToken)
                            } else {
                                this.softLogout()
                            }
                        } catch {
                            this.softLogout()
                        } finally {
                            this.isAlreadyFetchingAccessToken = false
                        }
                    }

                    return new Promise((resolve, reject) => {
                        this.addSubscriber((accessToken, err) => {
                            if (err) { reject(err); return }
                            originalRequest.headers = originalRequest.headers || {}
                            originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`.trim()
                            resolve(axios(originalRequest))
                        })
                    })
                }

                return Promise.reject(error)
            }
        )
    }

    onAccessTokenFetched(accessToken) {
        this.subscribers.forEach((cb) => cb(accessToken, null))
        this.subscribers = []
    }

    onRefreshFailed(error) {
        this.subscribers.forEach((cb) => cb(null, error))
        this.subscribers = []
    }

    addSubscriber(cb) { this.subscribers.push(cb) }

    getToken() {
        if (this._accessToken) return this._accessToken
        const state = store?.getState?.()
        return state?.auth?.[this.jwtConfig.storageTokenKeyName] || null
    }

    setToken(value) {
        this._accessToken = value || null
        store?.dispatch?.({ type: 'authentication/handleRefreshToken', payload: value })
    }

    getRefreshToken() { return null }
    setRefreshToken() {}

    // Rechaza requests en cola + limpia Redux. React Router redirige a /login.
    softLogout() {
        this.onRefreshFailed(new Error('SESSION_EXPIRED'))
        store?.dispatch?.({ type: 'authentication/handleLogout' })
    }

    login(...args) {
        return axios.put(this.jwtConfig.loginEndpoint, ...args)
    }

    register(...args) {
        return axios.put(this.jwtConfig.registerEndpoint + uuidv4(), ...args)
    }

    refreshToken() {
        return axios.post(this.jwtConfig.refreshEndpoint, {})
    }

    get(url, config = {}) { return axios.get(this.jwtConfig.backEndpoint + url, config) }
    patch(url, data = undefined, config = {}) { return axios.patch(this.jwtConfig.backEndpoint + url, data, config) }
    put(url, ...args) { return axios.put(this.jwtConfig.backEndpoint + url, ...args) }
    post(url, ...args) { return axios.post(this.jwtConfig.backEndpoint + url, ...args) }
    delete(url, data = undefined, config = {}) { return axios.delete(this.jwtConfig.backEndpoint + url, data, config) }
}
